import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const steps = [
    {
      title: 'Sensor Sends Data',
      description: 'Real-time data collection from various sensor types',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      title: 'Edge Filtering',
      description: 'Low latency processing at the edge for immediate response',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      ),
    },
    {
      title: 'CIVIONIKS API Layer',
      description: 'Secure and scalable data integration platform',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'AI Engine for Smart Analysis',
      description: 'Advanced machine learning algorithms process and analyze data',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
    },
    {
      title: 'Dashboard & Alerts',
      description: 'Actionable insights delivered through intuitive interfaces',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM4 19h6l-6 6v-6zM9 3h6l-6 6V3z" />
        </svg>
      ),
    },
  ];

  return (
    <section id="how-it-works" className="section-padding-mobile bg-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-12 w-48 h-48 xs:-left-24 xs:w-96 xs:h-96 bg-primary-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-12 w-48 h-48 xs:-right-24 xs:w-96 xs:h-96 bg-accent-100/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container-mobile relative">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 xs:mb-16 lg:mb-20"
        >

          <h2 id="how-it-works-heading" className="text-mobile-title font-bold text-gray-900 mb-4 xs:mb-6">
            How It Works – From{' '}
            <span className="gradient-text">Sensor to Insights</span>
          </h2>
          <p className="text-mobile-body text-gray-600 max-w-4xl mx-auto">
            Our streamlined process transforms raw sensor data into actionable intelligence
          </p>
        </motion.div>

        {/* Desktop Flow */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Flow line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary-200 via-primary-500 to-primary-200 transform -translate-y-1/2"></div>
            
            <div className="relative flex justify-between items-center">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className="flex flex-col items-center max-w-xs"
                >
                  {/* Icon circle */}
                  <div className="w-16 h-16 xs:w-20 xs:h-20 bg-white border-4 border-primary-500 rounded-full flex items-center justify-center text-primary-600 mb-4 xs:mb-6 shadow-mobile-large xs:shadow-lg z-10">
                    {step.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-base xs:text-lg font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Flow */}
        <div className="lg:hidden space-y-6 xs:space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="flex items-center space-x-3 xs:space-x-4"
            >
              {/* Icon */}
              <div className="w-12 h-12 xs:w-16 xs:h-16 bg-primary-100 rounded-full xs:rounded-xl flex items-center justify-center text-primary-600 flex-shrink-0 shadow-mobile-soft xs:shadow-soft">
                {step.icon}
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <h3 className="text-base xs:text-lg font-semibold text-gray-900 mb-1 xs:mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm xs:text-base">
                  {step.description}
                </p>
              </div>

              {/* Arrow for all items */}
              <div className="text-primary-300 flex-shrink-0">
                <svg className="w-4 h-4 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-center mt-12 xs:mt-16"
        >
          <div className="bg-white rounded-2xl xs:rounded-3xl shadow-mobile-medium xs:shadow-medium p-6 xs:p-8 lg:p-12 border border-gray-100 max-w-3xl mx-auto">
            <p className="text-base xs:text-lg text-gray-600 mb-6 xs:mb-8">
              Ready to transform your infrastructure monitoring?
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('api-access');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary-mobile group min-h-touch-md"
            >
              Get Started with Our API
              <svg className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Section divider */}
      <div className="section-divider mt-16 xs:mt-20 lg:mt-24"></div>
    </section>
  );
};

export default HowItWorks; 